import { useCallback, useEffect, useRef, useState } from "react";

export type RouteSearchCommitOptions = { replace: boolean };

export type UseDebouncedRouteSearchOptions = {
  value: string | undefined;
  routeSearchKey: string;
  onCommit: (value: string | undefined, options: RouteSearchCommitOptions) => void;
  debounceMs?: number;
};

export type DebouncedRouteSearch = {
  inputValue: string;
  setInputValue: (value: string) => void;
  beginEdit: () => void;
  endEdit: () => void;
  cancelPending: () => void;
  takePending: () => string | undefined;
};

const DEFAULT_DEBOUNCE_MS = 250;
const NO_EXPECTED_VALUE = Symbol("no expected route search value");

export const optionalRouteSearchString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized === "" ? undefined : normalized;
};

export const routeSearchKey = (search: Readonly<Record<string, unknown>>): string =>
  JSON.stringify(
    Object.entries(search)
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right)),
  );

export const useDebouncedRouteSearch = ({ value, routeSearchKey: sourceKey, onCommit, debounceMs = DEFAULT_DEBOUNCE_MS }: UseDebouncedRouteSearchOptions): DebouncedRouteSearch => {
  const committedValue = optionalRouteSearchString(value);
  const [inputValue, setInputValueState] = useState(committedValue ?? "");
  const inputValueRef = useRef(inputValue);
  const committedValueRef = useRef(committedValue);
  const sourceKeyRef = useRef(sourceKey);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expectedValueRef = useRef<string | undefined | typeof NO_EXPECTED_VALUE>(NO_EXPECTED_VALUE);
  const onCommitRef = useRef(onCommit);
  const editingRef = useRef(false);
  const committedInSessionRef = useRef(false);
  onCommitRef.current = onCommit;

  const clearTimeoutRef = useCallback((): void => {
    if (timeoutRef.current === null) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const beginEdit = useCallback((): void => {
    if (editingRef.current) return;
    editingRef.current = true;
    committedInSessionRef.current = false;
  }, []);

  const endEdit = useCallback((): void => {
    editingRef.current = false;
  }, []);

  const setInputValue = useCallback(
    (nextValue: string): void => {
      beginEdit();
      inputValueRef.current = nextValue;
      setInputValueState(nextValue);
    },
    [beginEdit],
  );

  const resetToCommittedValue = useCallback((): void => {
    const nextValue = committedValueRef.current ?? "";
    inputValueRef.current = nextValue;
    setInputValueState(nextValue);
    editingRef.current = false;
    committedInSessionRef.current = false;
  }, []);

  const cancelPending = useCallback((): void => {
    clearTimeoutRef();
    expectedValueRef.current = NO_EXPECTED_VALUE;
    resetToCommittedValue();
  }, [clearTimeoutRef, resetToCommittedValue]);

  const takePending = useCallback((): string | undefined => {
    clearTimeoutRef();
    expectedValueRef.current = NO_EXPECTED_VALUE;
    editingRef.current = false;
    committedInSessionRef.current = false;
    return optionalRouteSearchString(inputValueRef.current);
  }, [clearTimeoutRef]);

  useEffect(() => {
    if (sourceKeyRef.current === sourceKey) {
      committedValueRef.current = committedValue;
      return;
    }

    sourceKeyRef.current = sourceKey;
    committedValueRef.current = committedValue;

    if (expectedValueRef.current !== NO_EXPECTED_VALUE && expectedValueRef.current === committedValue) {
      expectedValueRef.current = NO_EXPECTED_VALUE;
      return;
    }

    clearTimeoutRef();
    expectedValueRef.current = NO_EXPECTED_VALUE;
    resetToCommittedValue();
  }, [clearTimeoutRef, committedValue, resetToCommittedValue, sourceKey]);

  useEffect(() => {
    clearTimeoutRef();
    const normalizedInput = optionalRouteSearchString(inputValue);
    if (normalizedInput === committedValueRef.current) return;

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const nextValue = optionalRouteSearchString(inputValueRef.current);
      if (nextValue === committedValueRef.current) return;

      const replace = committedInSessionRef.current;
      committedInSessionRef.current = true;
      expectedValueRef.current = nextValue;
      onCommitRef.current(nextValue, { replace });
    }, debounceMs);

    return clearTimeoutRef;
  }, [clearTimeoutRef, debounceMs, inputValue]);

  useEffect(() => clearTimeoutRef, [clearTimeoutRef]);

  return { inputValue, setInputValue, beginEdit, endEdit, cancelPending, takePending };
};
